import { createContext, use, useEffect, useMemo, useState } from "react";

interface ReviewTimeContextType {
  reviewTime: number;
}

const ReviewTimeContext = createContext<ReviewTimeContextType>({ reviewTime: Date.now() });

/**
 * We determine the cards that need to be reviewed by comparing the current time to the "nextReview" field of each card.
 * This context ensures that we use a consistent "current time" across the app so that we get fresh data without constantly re-fetching it.
 */
export const ReviewTimeProvider = ({ children }: { children: React.ReactNode }) => {
  const [reviewTime, setReviewTime] = useState(Date.now());
  // Increment reviewTime every 12 hours.
  // This will trigger a global re-render of the app, which is annoying to the user (dialogs will close, current reviews will reset, etc.)
  // So we only do this very infrequently in the off chance that the user has the app open for an abnormally long time.
  useEffect(() => {
    const interval = setInterval(
      () => {
        setReviewTime(Date.now());
      },
      12 * 60 * 60 * 1000 // 12 hours
    );
    return () => clearInterval(interval);
  }, [setReviewTime]);

  const contextValue = useMemo(() => ({ reviewTime }), [reviewTime]);

  return <ReviewTimeContext value={contextValue}>{children}</ReviewTimeContext>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useReviewTime(): number {
  const context = use(ReviewTimeContext);
  return context.reviewTime;
}
